import { useEffect, useState } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DocRender } from 'react-doc-render';
import { tokens } from '../../../global/theme/tokens';
import { TextPreview, isTextSource, isXmlSource } from '../ExoFilePreview';
import XRechnungPreview from '../../special/XRechnungPreview';
import { extractEmbeddedInvoiceXml } from '../../../utils/xrechnung';
import { entryKind, entryLabel } from './explorerUtils';
import CsvPreview from './CsvPreview';
import EmlPreview from './EmlPreview';

// Fallback mapping for when the API reports a generic mime type.
const EXTENSION_MIME = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  webp: 'image/webp',
  avif: 'image/avif',
  bmp: 'image/bmp',
  ico: 'image/x-icon',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  heif: 'image/heif',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  rtf: 'application/rtf',
  csv: 'text/csv',
  txt: 'text/plain',
  log: 'text/plain',
  md: 'text/markdown',
  xml: 'application/xml',
  json: 'application/json',
  eml: 'message/rfc822',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  m4v: 'video/mp4',
  webm: 'video/webm',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
};

// Mime types react-doc-render can actually render (spreadsheets via SheetJS,
// docx via mammoth). ppt/doc/rtf are not supported and fall through to the icon.
const DOC_RENDER_MIMES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

// Generic mime types some backends / object stores report when they don't
// know the real one; for these the file extension is more trustworthy.
const GENERIC_MIMES = [
  'application/octet-stream',
  'binary/octet-stream',
  'application/x-download',
  'application/download',
];

function effectiveMime(mimeType, name) {
  const type = (mimeType || '').toLowerCase();
  if (type && !GENERIC_MIMES.includes(type)) return type;
  const extension = ((name || '').split('.').pop() || '').toLowerCase();
  return EXTENSION_MIME[extension] || type;
}

const PreviewFallback = ({ entry }) => {
  const { t } = useTranslation();
  const kind = entryKind(entry);
  return (
    <Box className="w-full h-full flex flex-col items-center justify-center gap-2 opacity-60">
      <kind.Icon sx={{ fontSize: '44px', color: kind.color }} />
      <Typography variant="body2">{t('No Preview')}</Typography>
    </Box>
  );
};

/**
 * Inline full-size preview of a network file (no dialog): images and videos
 * natively, PDFs via the browser viewer (with a toggle to the embedded
 * ZUGFeRD/XRechnung XML when present), text/XML and Office documents via the
 * shared preview renderers.
 */
const ExplorerFilePreview = ({ src, mimeType = '', entry }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const type = effectiveMime(mimeType, entry.name);
  const isPdf = type === 'application/pdf';

  // Formats the browser cannot decode (e.g. HEIC outside Safari, or video
  // codecs like ProRes/HEVC in .mov files) fall back to the icon view via
  // the media error handlers.
  const [imgFailed, setImgFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  useEffect(() => {
    setImgFailed(false);
    setVideoFailed(false);
  }, [src]);

  // ZUGFeRD / Factur-X: PDFs can carry the invoice as embedded XML
  const [invoiceXml, setInvoiceXml] = useState(null);
  const [pdfView, setPdfView] = useState('pdf');
  useEffect(() => {
    let cancelled = false;
    setInvoiceXml(null);
    setPdfView('pdf');
    if (!isPdf || !src) return;
    extractEmbeddedInvoiceXml(src)
      .then((result) => {
        if (!cancelled && result) setInvoiceXml(result.xml);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [src, isPdf]);

  if (type.startsWith('image/')) {
    if (imgFailed) return <PreviewFallback entry={entry} />;
    return (
      <Box className="w-full h-full flex items-center justify-center p-3">
        <img
          src={src}
          alt={entryLabel(entry)}
          onError={() => setImgFailed(true)}
          className="max-w-full max-h-full object-contain rounded-lg"
        />
      </Box>
    );
  }

  if (isPdf)
    return (
      <Box className="w-full h-full flex flex-col overflow-hidden">
        {invoiceXml && (
          <Box
            className="w-full flex justify-center p-1"
            sx={{ bgcolor: colors.glass }}
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={pdfView}
              onChange={(e, value) => value && setPdfView(value)}
            >
              <Tooltip title={t('Show PDF')}>
                <ToggleButton value="pdf">PDF</ToggleButton>
              </Tooltip>
              <Tooltip title={t('Show e-invoice data (XML)')}>
                <ToggleButton value="xml">XML</ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>
        )}
        <Box className="flex-1 min-h-0 overflow-hidden">
          {invoiceXml && pdfView === 'xml' ? (
            <XRechnungPreview xml={invoiceXml} />
          ) : (
            <iframe
              src={src}
              title={entryLabel(entry)}
              className="w-full h-full"
              style={{ border: 'none' }}
            />
          )}
        </Box>
      </Box>
    );

  if (type === 'message/rfc822') return <EmlPreview src={src} />;

  if (type === 'text/csv') return <CsvPreview src={src} />;

  if (isXmlSource(src, type) || isTextSource(src, type))
    return (
      <Box className="w-full h-full flex flex-col overflow-hidden">
        <TextPreview src={src} />
      </Box>
    );

  if (DOC_RENDER_MIMES.includes(type))
    return (
      <Box className="w-full h-full overflow-auto">
        <DocRender uri={src} mime={type} />
      </Box>
    );

  if (type.startsWith('video/')) {
    if (videoFailed) return <PreviewFallback entry={entry} />;
    return (
      <video
        controls
        src={src}
        onError={() => setVideoFailed(true)}
        className="w-full h-full object-contain"
      />
    );
  }

  if (type.startsWith('audio/'))
    return (
      <Box className="w-full h-full flex items-center justify-center p-4">
        <audio controls src={src} className="w-full" />
      </Box>
    );

  return <PreviewFallback entry={entry} />;
};

export default ExplorerFilePreview;
